import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Label } from "@woodmac-hycb/form-input/lib";
import { Highchart } from "@woodmac-hycb/highcharts";
import { Col, Row } from "@woodmac-hycb/layout";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getEntityGeneration, getHitPointsForAlerts } from "../services/EntityService";
import { Button } from "@woodmac-hycb/button";
import { useToggleRegion } from "../hooks/useToggleRegion";
import { useAuth0 } from "@auth0/auth0-react";
import { Text } from "@woodmac-hycb/text";
import { useRef } from "react";
import useAlertHitPoints from "../hooks/UseAlertHitPoints";
import moment from "moment/moment";

function AlertsPlot({ alertsToPlot, selectedPlant }) {

  const dateNow = useMemo(() => moment(), []);
  const dateWeekAgo = useMemo(() => moment().subtract(7, 'day'), []);
  const [fromDate, setFromDate] = useState(dateWeekAgo);
  const [toDate, setToDate] = useState(dateNow);
  const [daysOutOfRange, setDaysOutOfRange] = useState(false); // flag to show an error if the from and start date are not within 7 days

  const [generationHistory, setGenerationHistory] = useState([]);

  const prevAlertsToPlot = useRef(null);

  const { isRegionNa } = useToggleRegion();
  const { getAccessTokenSilently } = useAuth0();

  const getAlertHits = useCallback(async (alertsToPlot, plant, start, end) => {
    if (alertsToPlot.length > 0) {
      const ids = alertsToPlot.map(a => a.id);
      const idsString = ids.join();
      const token = await getAccessTokenSilently();
      const response = await getHitPointsForAlerts(plant.entityId, idsString, start.format("YYYY-MM-DD HH:mm"), end.format("YYYY-MM-DD HH:mm"), isRegionNa ? "na" : "eu", token)
      if (response.status === 200) {
        const hitPoints = response.data;
        return hitPoints;
      }
    } else {
      return {};
    }
  }, [getAccessTokenSilently, isRegionNa])

  const { hitPointsDict, setHitPointsDict } = useAlertHitPoints({ alertsToPlot, fromDate, toDate, selectedPlant, isRegionNa, getAlertHits, prevAlertsToPlot });

  const getGenerationData = useCallback(async (plantSelected, startDate, endDate) => {
    const token = await getAccessTokenSilently();
    const response = await getEntityGeneration(plantSelected.entityId, startDate.format("YYYY-MM-DD HH:mm"), endDate.format("YYYY-MM-DD HH:mm"), isRegionNa ? "na" : "eu", token)
    if (response.status === 200) {
      const genData = response.data;
      return genData;
    }
  }, [getAccessTokenSilently, isRegionNa])

  useEffect(() => {
    if (selectedPlant) {
      getGenerationData(selectedPlant, dateWeekAgo, dateNow)
        .then(genData => setGenerationHistory(genData));
      setToDate(dateNow);
      setFromDate(dateWeekAgo);
      setHitPointsDict({});
      prevAlertsToPlot.current = null;
    }
  }, [selectedPlant, dateWeekAgo, dateNow, getGenerationData, setHitPointsDict])

  const findHitPoints = (a) => {
    const dataPoints = hitPointsDict[a.id];
    if (dataPoints) {
      return dataPoints.map((dp) => ([Date.parse(dp.timeStamp), dp.mwDelivered]));
    } else {
      return [];
    }
  }

  function fnv1aHash(string) {
    const FNV_PRIME = 16777619;
    const OFFSET_BASIS = 2166136261;

    let hash = OFFSET_BASIS;

    for (let i = 0; i < string.length; i++) {
      hash ^= string.charCodeAt(i);
      hash *= FNV_PRIME;
    }

    return hash >>> 0; // Ensure unsigned 32-bit integer value
  }

  const getColorFromString = (str) => {
    const hash = fnv1aHash(str)
    let color = '#'
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF
      color += ('00' + value.toString(16)).substr(-2)
    }
    return color;
  }

  const makeSeries = () => {
    return alertsToPlot.map((a, index) => (
      {
        name: a.title,
        lineWidth: 0,
        data: findHitPoints(a),
        color: getColorFromString(a.title),
        pointStart: Date.parse(fromDate),
        zIndex: alertsToPlot.length - index,
        states: {
          hover: {
            enabled: false
          }
        },
        marker: {
          symbol: 'circle',
          enabled: true
        }
      }
    ))
  }

  const options = {
    chart: {
      type: "line",
      height: 500
    },
    title: {
      text: "Plotted Alerts",
    },
    yAxis: {
      title: {
        text: 'Megawatts'
      }
    },

    xAxis: {
      title: {
        text: 'Date'
      },
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%Y %b %d'
      }
    },
    time: {
      useUTC: false
    },
    series: [
      ...makeSeries(),
      {
        name: 'Plant Output',
        data: generationHistory.map(e => ([Date.parse(e.timeStamp), e.mwDelivered])),
        color: 'black',
        pointStart: Date.parse(fromDate),
        lineWidth: 2,
        zIndex: 0,
        marker: {
          symbol: 'square',
          enabled: generationHistory > 30
        }
      }
    ],
  };

  const checkDateRange = (start, end) => {
    const dayDifference = moment(end).diff(moment(start), 'days');
    if (dayDifference > 7) {
      return false;
    }
    return true;
  }

  const handleSubmit = async (selectedPlant, start, end) => {
    if (checkDateRange(start, end)) {
      setDaysOutOfRange(false);
      const hitsPromise = getAlertHits(alertsToPlot, selectedPlant, start, end);
      const genDataPromise = getGenerationData(selectedPlant, start, end);
      const [hitPoints, genData] = await Promise.all([hitsPromise, genDataPromise]);
      setGenerationHistory(genData);
      setHitPointsDict(hitPoints);
      prevAlertsToPlot.current = alertsToPlot;
    } else {
      setDaysOutOfRange(true);
    }
  }

  return (
    <Col gap={16} flex=".75">
      <Highchart options={options} onLoad={chart => null}></Highchart>
      <Row gap={64} justifyContent="center">
        <Col gap={16}>
          <Row gap={32} justifyContent="space-between">
            <Row gap={8} alignItems="center">
              <Label>From</Label>
              <DatePicker className='datepicker' selected={fromDate.toDate()} onChange={(d) => (setFromDate(moment(d)))} dateFormat={'yyyy/MM/dd HH:mm'} showTimeInput timeFormat="HH:mm"></DatePicker>
            </Row>
            <Row gap={8} alignItems="center">
              <Label>To</Label>
              <DatePicker className='datepicker' selected={toDate.toDate()} onChange={(d) => (setToDate(moment(d)))} dateFormat={'yyyy/MM/dd HH:mm'} showTimeInput timeFormat="HH:mm" ></DatePicker>
            </Row>
          </Row>
          <Row justifyContent="center">
            {daysOutOfRange && <Text justifyContent="space-between" color="error100" fontSize="ty80">From and To dates can't be more than 7 days apart</Text>}
          </Row>
        </Col>
        <Button onClick={() => handleSubmit(selectedPlant, fromDate, toDate)}>Submit</Button>
      </Row>
    </Col>
  )
}
export default AlertsPlot;